import { OccupancyLease, OccupancyUnit } from './types'
import { isUnassignedOccupancyLease } from './occupancyLease'
import {
    LIFESTYLE_COLUMNS,
    LifestyleFieldId,
    genderCellConflicts,
    genderGroupConflict,
    pairLifestyleScore,
    petCellConflicts,
    petGroupConflict,
} from './occupancyLifestyle'

export type UnitMatchKind = 'none' | 'score' | 'conflict'

export interface UnitMatchScore {
    kind: UnitMatchKind
    score: number | null
    label: string
    reason: string
    tooltip: string
}

function normalizeEmail(value?: string | null) {
    return (value || '').trim().toLowerCase()
}

function peopleInUnit(occupants: Array<OccupancyLease | null>) {
    return occupants.filter((row): row is OccupancyLease => row != null)
}

function leaseById(leases: OccupancyLease[]) {
    const map = new Map<number, OccupancyLease>()
    for (const lease of leases) map.set(lease.id, lease)
    return map
}

function mutualPeerIds(lease: OccupancyLease) {
    return (lease.occupancy_facts?.mutual_with || []).map(
        (peer) => peer.lease_id,
    )
}

function mutualCliqueIds(
    people: OccupancyLease[],
    allById: Map<number, OccupancyLease>,
) {
    const seeds = people.filter((row) => mutualPeerIds(row).length > 0)
    if (!seeds.length) return null
    const seen = new Set<number>()
    const queue = seeds.map((row) => row.id)
    while (queue.length) {
        const id = queue.pop() as number
        if (seen.has(id)) continue
        seen.add(id)
        const lease = allById.get(id) || people.find((row) => row.id === id)
        for (const peerId of lease ? mutualPeerIds(lease) : []) {
            if (!seen.has(peerId)) queue.push(peerId)
        }
    }
    return seen
}

function isCompleteRequestedPack(
    people: OccupancyLease[],
    allById: Map<number, OccupancyLease>,
) {
    if (people.length < 2) return false
    const clique = mutualCliqueIds(people, allById)
    if (!clique || clique.size < 2) return false
    const unitIds = new Set(people.map((row) => row.id))
    for (const id of Array.from(clique)) {
        if (!unitIds.has(id)) return false
    }
    return people.every((row) => clique.has(row.id))
}

function occupantEmails(people: OccupancyLease[]) {
    return new Set(
        people.map((row) => normalizeEmail(row.tenant_email)).filter(Boolean),
    )
}

function requestOverlayForPerson(
    person: OccupancyLease,
    people: OccupancyLease[],
) {
    const unitIds = new Set(people.map((row) => row.id))
    const emails = occupantEmails(people)
    const peers = mutualPeerIds(person)
    const hasMutual = peers.length > 0
    let allMutualSeated = true
    let missingRequested = false
    let oneWayTogether = false

    if (hasMutual && peers.some((id) => !unitIds.has(id))) {
        allMutualSeated = false
        missingRequested = true
    }
    for (const row of person.occupancy_facts?.requested || []) {
        const email = normalizeEmail(row.email)
        if (!email) continue
        const inUnit = emails.has(email)
        if (row.status !== 'mutual' && inUnit) oneWayTogether = true
        if (row.status !== 'mutual' && !inUnit) missingRequested = true
    }

    let delta = 0
    const notes: string[] = []
    if (hasMutual && allMutualSeated && !missingRequested) {
        delta += 10
        notes.push('Requested roommates seated together')
    }
    if (oneWayTogether) {
        delta += 5
        notes.push('One-way request in this unit')
    }
    if (missingRequested) {
        delta -= 15
        notes.push('Requested roommate is not in this unit')
    }
    return { delta, notes, hasMutual, allMutualSeated }
}

function requestOverlay(people: OccupancyLease[]) {
    const unitIds = new Set(people.map((row) => row.id))
    const emails = occupantEmails(people)
    let allMutualSeated = true
    let hasMutual = false
    let missingRequested = false
    let oneWayTogether = false

    for (const person of people) {
        const peers = mutualPeerIds(person)
        if (peers.length) {
            hasMutual = true
            if (peers.some((id) => !unitIds.has(id))) {
                allMutualSeated = false
                missingRequested = true
            }
        }
        for (const row of person.occupancy_facts?.requested || []) {
            const email = normalizeEmail(row.email)
            if (!email) continue
            const inUnit = emails.has(email)
            if (row.status !== 'mutual' && inUnit) oneWayTogether = true
            if (row.status !== 'mutual' && !inUnit) missingRequested = true
        }
    }

    let delta = 0
    const notes: string[] = []
    if (hasMutual && allMutualSeated && !missingRequested) {
        delta += 10
        notes.push('Requested roommates seated together')
    }
    if (oneWayTogether) {
        delta += 5
        notes.push('One-way request in this unit')
    }
    if (missingRequested) {
        delta -= 15
        notes.push('Requested roommate is not in this unit')
    }
    return { delta, notes, hasMutual, allMutualSeated }
}

type LifestylePair = {
    leftId: number
    rightId: number
    score: number
    hardFail: boolean
    dims: Record<LifestyleFieldId, number>
}

function lifestylePairs(scored: OccupancyLease[]): LifestylePair[] {
    const pairs: LifestylePair[] = []
    for (let i = 0; i < scored.length; i += 1) {
        for (let j = i + 1; j < scored.length; j += 1) {
            const pair = pairLifestyleScore(scored[i], scored[j])
            pairs.push({
                leftId: scored[i].id,
                rightId: scored[j].id,
                ...pair,
            })
        }
    }
    return pairs
}

function scoreLifestylePairs(
    pairs: Array<{
        score: number
        hardFail: boolean
        dims: Record<LifestyleFieldId, number>
    }>,
    overlay: { delta: number; notes: string[] },
    extraNotes: string[],
    conflict: { hardFail: boolean; pet: boolean; gender: boolean },
): UnitMatchScore {
    const dimLine = averageDims(pairs)
    const extras = [dimLine, ...overlay.notes, ...extraNotes]

    if (pairs.every((pair) => !pair.hardFail && pair.score >= 0.999)) {
        return {
            kind: 'score',
            score: 100,
            label: '100',
            reason: 'Matching lifestyle answers',
            tooltip: bandTooltip(100, extras),
        }
    }

    const values = pairs.map((pair) => pair.score)
    const mean = values.reduce((sum, value) => sum + value, 0) / values.length
    const min = Math.min(...values)
    const blended = 0.7 * mean + 0.3 * min
    let display = Math.round(100 * blended) + overlay.delta
    if (overlay.delta > 0) display = Math.min(display, 99)
    display = Math.max(0, Math.min(100, display))

    if (conflict.hardFail) {
        display = Math.min(display, 15)
        extras.unshift('Must-have lifestyle clash')
        return {
            kind: 'conflict',
            score: display,
            label: 'Conflict',
            reason: 'Must-have lifestyle clash',
            tooltip: bandTooltip(display, extras),
        }
    }
    if (conflict.pet) {
        display = Math.min(display, 20)
        extras.unshift('Pet comfort conflict')
        return {
            kind: 'conflict',
            score: display,
            label: 'Conflict',
            reason: 'Pet comfort conflict',
            tooltip: bandTooltip(display, extras),
        }
    }
    if (conflict.gender) {
        display = Math.min(display, 20)
        extras.unshift('Gender / coed conflict')
        return {
            kind: 'conflict',
            score: display,
            label: 'Conflict',
            reason: 'Gender / coed conflict',
            tooltip: bandTooltip(display, extras),
        }
    }

    return {
        kind: 'score',
        score: display,
        label: String(display),
        reason: 'Lifestyle match',
        tooltip: bandTooltip(display, extras),
    }
}

const PACK_TOGETHER: UnitMatchScore = {
    kind: 'score',
    score: 100,
    label: '100',
    reason: 'Requested roommates together',
    tooltip: 'Requested roommate group is together in this unit',
}

function averageDims(pairs: Array<{ dims: Record<LifestyleFieldId, number> }>) {
    const totals = {} as Record<LifestyleFieldId, number>
    for (const column of LIFESTYLE_COLUMNS) totals[column.id] = 0
    for (const pair of pairs) {
        for (const column of LIFESTYLE_COLUMNS) {
            totals[column.id] += pair.dims[column.id] ?? 0
        }
    }
    return LIFESTYLE_COLUMNS.map((column) => {
        const value = pairs.length ? totals[column.id] / pairs.length : 0
        return `${column.label} ${value.toFixed(1)}`
    }).join(' · ')
}

function bandTooltip(score: number, extras: string[]) {
    const lines = [`Roommate match ${score}`, ...extras]
    return lines.join('\n')
}

export function unitMatchScore(
    occupants: Array<OccupancyLease | null>,
    allLeases: OccupancyLease[],
): UnitMatchScore {
    const people = peopleInUnit(occupants)
    if (people.length < 2) {
        return {
            kind: 'none',
            score: null,
            label: '—',
            reason: 'Need two people to score',
            tooltip: 'Roommate match needs at least two people in the unit',
        }
    }

    const allById = leaseById(allLeases)
    if (isCompleteRequestedPack(people, allById)) return PACK_TOGETHER

    const petConflict = petGroupConflict(occupants)
    const genderConflict = genderGroupConflict(occupants)
    const scored = people.filter((row) => row.lifestyle_complete)
    const overlay = requestOverlay(people)

    if (scored.length < 2) {
        return {
            kind: 'none',
            score: null,
            label: '—',
            reason:
                scored.length === 0
                    ? 'Lifestyle incomplete'
                    : `${scored.length}/${people.length} answered lifestyle`,
            tooltip:
                scored.length === 0
                    ? 'Lifestyle incomplete — not enough answers to score'
                    : `${scored.length} of ${people.length} answered lifestyle`,
        }
    }

    const pairs = lifestylePairs(scored)
    const extraNotes =
        scored.length < people.length
            ? [`${scored.length}/${people.length} answered lifestyle`]
            : []
    return scoreLifestylePairs(pairs, overlay, extraNotes, {
        hardFail: pairs.some((pair) => pair.hardFail),
        pet: petConflict,
        gender: genderConflict,
    })
}

const PERSON_NEEDS_ROOMMATE: UnitMatchScore = {
    kind: 'none',
    score: null,
    label: '—',
    reason: 'Need a roommate to score',
    tooltip: 'Roommate match needs at least two people in the unit',
}

export function personMatchScore(
    person: OccupancyLease,
    occupants: Array<OccupancyLease | null>,
    allLeases: OccupancyLease[],
): UnitMatchScore {
    const people = peopleInUnit(occupants)
    if (people.length < 2) return PERSON_NEEDS_ROOMMATE

    const allById = leaseById(allLeases)
    if (isCompleteRequestedPack(people, allById)) return PACK_TOGETHER

    const scored = people.filter((row) => row.lifestyle_complete)
    if (!person.lifestyle_complete) {
        return {
            kind: 'none',
            score: null,
            label: '—',
            reason: 'Lifestyle incomplete',
            tooltip: 'Lifestyle incomplete — not enough answers to score',
        }
    }

    const pairs = lifestylePairs(scored).filter(
        (pair) => pair.leftId === person.id || pair.rightId === person.id,
    )
    if (!pairs.length) {
        return {
            kind: 'none',
            score: null,
            label: '—',
            reason: `${scored.length}/${people.length} answered lifestyle`,
            tooltip: `${scored.length} of ${people.length} answered lifestyle`,
        }
    }

    const extraNotes =
        scored.length < people.length
            ? [`${scored.length}/${people.length} answered lifestyle`]
            : []
    return scoreLifestylePairs(
        pairs,
        requestOverlayForPerson(person, people),
        extraNotes,
        {
            hardFail: pairs.some((pair) => pair.hardFail),
            pet: petCellConflicts(person, occupants),
            gender: genderCellConflicts(person, occupants),
        },
    )
}

export function matchScoreColor(result: UnitMatchScore) {
    if (result.kind === 'conflict') return '#be123c'
    if (result.kind === 'none' || result.score == null) return '#9ca3af'
    if (result.score >= 90) return '#047857'
    if (result.score >= 75) return '#0f766e'
    if (result.score >= 50) return '#b45309'
    return '#be123c'
}

export interface OccupancyUnitScoreRow {
    unitId: number
    vacant: number
    suggested: number
    match: UnitMatchScore
}

export function occupancyUnitScoreRows(
    units: OccupancyUnit[],
    leases: OccupancyLease[],
): OccupancyUnitScoreRow[] {
    const leaseByArea = new Map<number, OccupancyLease>()
    for (const lease of leases) {
        if (lease.area_id != null) leaseByArea.set(lease.area_id, lease)
    }
    return units.map((unit) => {
        const occupants = unit.areas.map(
            (area) => leaseByArea.get(area.id) ?? null,
        )
        let filled = 0
        let suggested = 0
        for (const lease of occupants) {
            if (!lease) continue
            filled += 1
            if (lease.occupancy_assignment === 'suggested') suggested += 1
        }
        return {
            unitId: unit.id,
            vacant: unit.areas.length - filled,
            suggested,
            match: unitMatchScore(occupants, leases),
        }
    })
}

function median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    if (sorted.length % 2 === 1) return sorted[mid]
    return (sorted[mid - 1] + sorted[mid]) / 2
}

export interface OccupancyChromeStats {
    unassigned: number
    drafts: number
    openBeds: number
    conflicts: number
    scoredCount: number
    minScore: number | null
    minKind: UnitMatchKind | null
    minUnitId: number | null
    averageScore: number | null
    medianScore: number | null
}

export function occupancyChromeStats(
    units: OccupancyUnit[],
    leases: OccupancyLease[],
): OccupancyChromeStats {
    const rows = occupancyUnitScoreRows(units, leases)
    const scored = rows.filter(
        (row) => row.match.score != null && row.match.kind !== 'none',
    )
    const scores = scored.map((row) => row.match.score as number)
    let minScore: number | null = null
    let minKind: UnitMatchKind | null = null
    let minUnitId: number | null = null
    for (const row of scored) {
        const score = row.match.score as number
        if (minScore == null || score < minScore) {
            minScore = score
            minKind = row.match.kind
            minUnitId = row.unitId
        }
    }
    return {
        unassigned: leases.filter(isUnassignedOccupancyLease).length,
        drafts: leases.filter(
            (lease) => lease.occupancy_assignment === 'suggested',
        ).length,
        openBeds: rows.reduce((sum, row) => sum + row.vacant, 0),
        conflicts: rows.filter((row) => row.match.kind === 'conflict').length,
        scoredCount: scored.length,
        minScore,
        minKind,
        minUnitId,
        averageScore:
            scores.length > 0
                ? scores.reduce((sum, value) => sum + value, 0) / scores.length
                : null,
        medianScore: scores.length > 0 ? median(scores) : null,
    }
}
